import { SampleSubType, SampleType, zSampleSubType, zSampleType } from '@resistapp/common/sampling-point-types';
import { chain, compact, Dictionary, get, keys, uniq } from 'lodash';
import { coerce } from 'semver';
import { z } from 'zod';
import { ReferenceByMAEC } from './api-types';
import { zL1Targets, zL2Targets } from './assays';
import { safeAssert } from './assert-utils';
import { Feature, LabFeature } from './features';
import {
  type LabWorkbookService,
  type ReceivedMaterial,
  zLabWorkbookServices,
  zReceivedMaterial,
} from './lab-dna-step';
import type { NcbiEsummaryRecord } from './ncbi-esummary-types';
import { RequiredFields } from './type-utils';

// Keep consistent with analysis
export enum BioRep {
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E',
  F = 'F',
  G = 'G',
  H = 'H',
  I = 'I',
  J = 'J',
  K = 'K',
  L = 'L',
  M = 'M',
  N = 'N',
  O = 'O',
  P = 'P',
  Q = 'Q',
  R = 'R',
  S = 'S',
  T = 'T',
  U = 'U',
  V = 'V',
  W = 'W',
  X = 'X',
  Y = 'Y',
  Z = 'Z',
}
export const zBioRep = z.nativeEnum(BioRep);

export enum AccessSubjectType {
  ORG = 'ORG',
  USER = 'USER',
}
export const zAccessSubjectType = z.nativeEnum(AccessSubjectType);

export enum AccessObjectType {
  PROJECT = 'PROJECT',
  SAMPLING_POINT = 'SAMPLING_POINT',
  SAMPLE = 'SAMPLE',
}
export const zAccessObjectType = z.nativeEnum(AccessObjectType);

export enum AccessLevel {
  OWNER = 'OWNER',
  READ = 'READ',
}
export const zAccessLevel = z.nativeEnum(AccessLevel);

export enum OrganisationType {
  PROVIDER = 'PROVIDER',
  LAB = 'LAB',
  CUSTOMER = 'CUSTOMER',
  RESISTOMAP = 'RESISTOMAP',
}
export const zOrganisationType = z.nativeEnum(OrganisationType);

export enum OrganisationUserRole {
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
}
export const zOrganisationUserRole = z.nativeEnum(OrganisationUserRole);

export interface UserProject {
  userId: number;
  projectId: number;
}

export interface OrganisationProject {
  organisationId: number;
  projectId: number;
}

export interface Access {
  objectId: number;
  objectType: AccessObjectType;
  subjectType: AccessSubjectType;
  subjectId: number;
  accessLevel: AccessLevel;
}

export interface OrganisationUser {
  organisationId: number;
  userId: number;
  role: OrganisationUserRole;
}

export interface RawUser {
  email: string;
  firstName: string;
  lastName: string;
}

export interface User extends RawUser {
  id: number;
  token?: string;
  telephoneNumber?: string | null;
  directProjectAccesses: number[];
  directSamplingPointAccesses: number[];
  directSampleAccesses: number[];
  organisations: Organisation[];
}

export interface Organisation {
  id: number;
  name: string;
  externalName: string | null;
  isDemo: boolean;
  signupCode: string | null;
  parentId: number | null;
  type: OrganisationType;
  country?: string | null;
  pickupAddressLine1?: string | null;
  pickupAddressLine2?: string | null;
  pickupAddressLine3?: string | null;
  pickupPostalCode?: string | null;
  pickupCity?: string | null;
  taxId?: string | null;
  projectAccesses: number[];
  samplingPointAccesses: number[];
  sampleAccesses: number[];
  features: Feature[];
  labFeatures: LabFeature[];
  defaultMetric: MetricMode | null;
  membershipRole?: OrganisationUserRole;
}

export type OrganisationSummary = Omit<Organisation, 'samplingPointAccesses' | 'sampleAccesses'>;

export enum ShipmentStatus {
  REQUESTED = 'REQUESTED',
  SCHEDULED = 'SCHEDULED',
  PROCESSED = 'PROCESSED',
  PICKED_UP = 'PICKED_UP',
  CANCELLED = 'CANCELLED',
  RECEIVED = 'RECEIVED',
}
export const zShipmentStatus = z.nativeEnum(ShipmentStatus);

export interface ShipmentItem {
  id: number;
  shipmentId: number;
  description: string;
  count: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
}

export interface Shipment {
  id: number;
  organisationId: number;
  labWorkbookId: string | null;
  createdBy: number;
  status: ShipmentStatus;
  pickupDate: string;
  pickupWindowStart: string;
  pickupWindowEnd: string;
  contactName: string;
  contactEmail: string;
  contactTelephoneNumber: string | null;
  pickupCountry: string | null;
  pickupAddressLine1: string | null;
  pickupAddressLine2: string | null;
  pickupAddressLine3: string | null;
  pickupPostalCode: string | null;
  pickupCity: string | null;
  taxId: string | null;
  packageWeightKg: number | null;
  packageLengthCm: number | null;
  packageWidthCm: number | null;
  packageHeightCm: number | null;
  confirmationUrl: string | null;
  quotaSpentAt: Date | null;
  items: ShipmentItem[];
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
}

export enum GenePanelState {
  INVALID = 'INVALID',
  VALID = 'VALID',
  LOCKED = 'LOCKED',
}

export interface GenePanel {
  id: number;
  organisationId: number;
  name: string;
  samples: number;
  genes: number;
  assays: string[];
  state: GenePanelState;
  selectionVersion: number;
  createdBy: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
}

export const zLevelsWithZoomableAreas = z.record(z.string(), z.array(z.number()));
export type LevelsWithZoomableAreas = z.infer<typeof zLevelsWithZoomableAreas>;

export enum ProjectSampleAction {
  ADD = 'ADD',
  MOVE = 'MOVE',
  REMOVE = 'REMOVE',
}

export enum ProjectSamplesAccessStrategy {
  LEAVE_UNCHANGED = 'LEAVE_UNCHANGED',
  GRANT_TARGET_ORGANISATIONS = 'GRANT_TARGET_ORGANISATIONS',
  SYNC_BETWEEN_PROJECTS = 'SYNC_BETWEEN_PROJECTS',
  REVOKE_ORPHAN_ACCESS = 'REVOKE_ORPHAN_ACCESS',
}
export const zProjectSamplesAccessStrategy = z.nativeEnum(ProjectSamplesAccessStrategy);

export const zProjectSamplesUpdate = z.record(
  z.string(),
  z.object({
    action: z.enum([ProjectSampleAction.ADD, ProjectSampleAction.MOVE, ProjectSampleAction.REMOVE]),
    sampleIds: z.array(z.number()),
    sourceProjectId: z.number(),
    accessStrategy: zProjectSamplesAccessStrategy.optional(),
    confirmProceedWithPartialVolumeNormalisation: z.boolean().optional(),
  }),
);
export type ProjectSamplesUpdate = z.infer<typeof zProjectSamplesUpdate>;

export interface Project {
  id: number;
  name: string;
  type: ProjectType;
  statusUserId?: number;
  statusNote?: string;
  filenames?: string[];
  warnings?: string[];
  driveLink?: string;
  customerSheetLink?: string;
  publicationName?: string;
  publicationUrl?: string;
  createdAt: Date | string;
  levelsWithZoomableAreas: PartialDict<PossibleZoomableLevel[]>; // { countryCode: [level1, level2, ...] }
  originalLevelsWithZoomableAreas: PartialDict<PossibleZoomableLevel[]>; // { countryCode: [level1, level2, ...] }
  userAccessCount?: number;
  defaultXAxisMode?: XAxisMode;
}

export const zDispenserPrefix = z.enum(['MSND', 'SCND']);
export type DispenserPrefix = z.infer<typeof zDispenserPrefix>;

export interface ChipInfo {
  runDate: Date;
  dispenserPrefix: DispenserPrefix;
  dispenser: number;
  cycler: number;
}

export interface ProjectData {
  ctsByChipName: Dictionary<CtCsvRow[]>;
  chipInfoByChipName: Dictionary<ChipInfo>;
  parsedSamples: SampleToBeCreated[];
  customerSheetLink: string | undefined;
  mmLot: string;
  waterLot: string | null;
  glycerolLot: string | null;
  antifoamBLot: string | null;

  /**
   * Use cases for previousLabSheetUrls - always used as a "sheet family" [labSheetUrl, ...previousLabSheetUrls]
   * - Reject samples that already have CTS data from foreign sheets (getExistingSamplesPreviouslyUploadedFromSheet).
   * - Sampling point reuse, edits and removals are restricted to the "sheet family" (findMatchingSamplingPoint, getSamplingPointUsageDetails, getForeignSampleForSmplPnt).
   * - Allow re-upload of chip (re-use of chip name) only within the "sheet family" (validateExistingChips).
   *   - Check for the sheet family before overwriting existing chips, cts, curves and raw-files (deleteChipsAndCts).
   */
  previousLabSheetUrls: string[];
}

export enum ProjectType {
  NORMAL = 'NORMAL',
  POOLED = 'POOLED',
  LEGACY = 'LEGACY',
}

export enum SampleStatus {
  SUBMITTED = 'SUBMITTED',
  UNAPPROVED = 'UNAPPROVED',
  FAILED = 'FAILED',
  DUPLICATE = 'DUPLICATE',
  POOLED = 'POOLED',
  APPROVED = 'APPROVED',
  APPROVED_WITH_ISSUES = 'APPROVED_WITH_ISSUES',
  APPROVED_SKIP_RESULT_STATISTICS = 'APPROVED_SKIP_RESULT_STATISTICS',
}

export function isPreQA(status: SampleStatus | null | undefined): boolean {
  return status === SampleStatus.SUBMITTED || status === SampleStatus.UNAPPROVED;
}

export function isPostQA(status: SampleStatus | null | undefined): boolean {
  return !isPreQA(status);
}

type SampleStatusCarrier = {
  status: SampleStatus | null | undefined;
};

export function isExternallyShownSample(sample: SampleStatusCarrier): boolean {
  return isPostQA(sample.status) && sample.status !== SampleStatus.FAILED;
}

export function filterExternallyShownSamples<T extends SampleStatusCarrier>(samples: T[]): T[] {
  return samples.filter(isExternallyShownSample);
}

export type CopiesByUB = Dictionary<Dictionary<number | undefined>>; // By sample UID, bioRep
export type ReplicatedSamples = FullSample[]; // Array of biologibal replica samples
export type FullSamplesByUID = Dictionary<ReplicatedSamples>; // By getBioRepGroupKey

export interface ReferenceLink {
  label: string;
  href: string;
}

export type AnalysisDescriptionBlock =
  | {
      type: 'paragraph';
      text: string;
      ctaHref?: string;
      ctaLabel?: string;
    }
  | {
      type: 'list';
      items: string[];
    };

export interface AnalysisDescriptionContent {
  intro: AnalysisDescriptionBlock[];
  dataProcessing?: AnalysisDescriptionBlock[];
  references: ReferenceLink[];
}

export enum ProcessKind {
  WATER_TREATMENT = 'WATER_TREATMENT',
}

export enum ProcessFlowKind {
  TREATMENT = 'TREATMENT',
}

export interface ProcessFlow {
  id: number;
  processId: number;
  fromPointId: number;
  toPointId: number;
  kind: ProcessFlowKind;
}

export interface Process {
  id: number;
  name: string;
  kind: ProcessKind;
  // Optional sampling point to use for marker placement or name fallback when only a subset is visible.
  anchorSamplingPointId: number | null;
  samplingPointIds?: number[];
  flows?: ProcessFlow[];
}

export interface ProcessSelection {
  selectedProcessId?: number;
  selectedStepPointId?: number;
  selectedFlowId?: number;
  comparedStepPointId?: number;
}

export interface FullProject extends Project {
  samplesByUID: FullSamplesByUID;
  chips: Chip[];
  linkedLabWorkbooks?: LabWorkbookLink[];
  processes?: Process[];
  // For supported projects and overview users only
  overviewReferencesByMAEC?: ReferenceByMAEC;
  researchReferencesByMAEC?: ReferenceByMAEC;
  // The focused term in focusedByUID attempts to communicate that
  // - samples filtered based on query parameters
  // - sample abundances are filtered based on query parameters
  //   - 16S rRNA either filtered out, or all other genes are filtered out
  focusedByUID?: FullSamplesByUID;
  assayVersion?: string;
  resultsVersion?: number;
  assayChangeNotification?: string;
  analysisDescription?: AnalysisDescriptionContent;
}

// Base type for samples (can be generated as an empty placeholder to get the ID for tubes)
export interface SamplePlaceholder {
  id: number;
}

export interface SampleNormalisationMetadata {
  // DNA measurements - diluted
  primDilutedDnaVolume: number | null;
  primDilutedDnaConcentration: number | null;
  primDilutedDnaConcentrationUnavailable?: boolean;
  primDilutedDnaQuality280: number | null;
  primDilutedDnaQuality230: number | null;
  // DNA measurements - eluted
  primElutedDnaQuality280: number | null;
  primElutedDnaQuality230: number | null;
  primElutedDnaConcentration: number | null;
  primElutedDnaVolume: number | null;
  auxExtractionFilteredVolumeMl?: number | null;
  auxExtractionElutionVolumeUl?: number | null;
  auxQaMeasuredVolumeUl?: number | null;
  auxQaConcentrationNgUl?: number | null;
  auxDilutionDesiredFinalVolumeUl?: number | null;
  auxDilutionSampleVolumeUl?: number | null;
  auxDilutionConcentrationNgUl?: number | null;
  // Specific for water samples
  filteredVolume: number | null; // copies/L
  // Wastewater treatment plan specific
  bod: number | null; // mg/L
  suspendedSolids: number | null; // mg/L
  operatedFlowRate: number | null; // L/h
  flowRate: number | null; // L/h
}

export interface SampleDataColumns extends SampleNormalisationMetadata {
  number: number | null;
  bioRep: BioRep | null;
  samplingPointId: number | null;
  sampleSheetId: string | null;
  panelId: number | null;
  time: Date | null;
  // lat/lon are kept in samples as supporting data for:
  // 1. Moving sampling points (e.g. ships, mobile sampling units)
  // 2. Large sampling points where biological samples are taken from significantly different locations
  specificLat: number | null;
  specificLon: number | null;
  status: SampleStatus | null;
  primDnaExtractionKit: string | null;
}

export interface SampleColumns extends SamplePlaceholder, SampleDataColumns {}

// TODO this started out as a Record type for creating a sample, but it has since been taken to use too braodly
export interface RawSample extends SampleColumns {
  number: number;
  bioRep: BioRep;
  samplingPointId: number;
  panelId: number | null;
  samplingPoint: RawSamplingPoint; // TODO this should be optional as it is not loaded by default
  projects: ProjectSample[];
  status: SampleStatus;
}

export interface ProjectSample {
  projectId: number;
  sampleId: number;
  id: number;
}

export interface Sample extends RawSample {
  samplingPoint: SamplingPoint | PooledSplPntHack; // TODO this should be optional as it is not loaded by default
}

export interface FullSample extends Sample {
  abundances: FullAbundance[]; // To be deprecated. UI still expects the legacy abundances result format, and it is populated by buildSamplesByUID

  /**
   * IDs of all chips that have any CTS rows for this sample. A sample can appear on multiple chips
   * when different assay sets are run on separate chips during the same lab analysis.
   * This can happen in corner cases, e.g. when the first chip has primer dilution failure for some samples,
   * and only the failed assays are re-analysed on a new chip.
   */
  chipIds?: number[];
  // Not needed by UI yet
  // assayResults: AssayResult[];
  // meanCts: MeanCt[];
}

// Type for samples during creation phase, before they have IDs assigned
// TODO: Remove 'id' from Omit once sample sheets provide real IDs
export interface SampleToBeCreated extends Omit<SampleColumns, 'projectId' | 'samplingPointId'> {
  samplingPoint: RawSamplingPoint;
}

export const frozenFieldsForApprovedSamples: Array<keyof SampleToBeCreated> = [
  'bioRep',
  'number',
  'primDilutedDnaVolume',
  'primDilutedDnaConcentration',
  'primDilutedDnaQuality280',
  'primDilutedDnaQuality230',
  'primElutedDnaQuality280',
  'primElutedDnaQuality230',
  'primElutedDnaConcentration',
  'primElutedDnaVolume',
  'filteredVolume',
  'bod',
  'suspendedSolids',
  'operatedFlowRate',
  'flowRate',
  'time',
];

export type SampleToBeCreatedWithoutId = Omit<SampleToBeCreated, 'id'>;

export function isPopulatedSample(sample: NullablePartial<SampleColumns>) {
  return (
    typeof sample.samplingPointId === 'number' && typeof sample.number === 'number' && typeof sample.status === 'string'
  );
}

export function assertPopulatedSample(samples: Array<NullablePartial<SampleColumns>>): asserts samples is RawSample[] {
  samples.forEach((sample: NullablePartial<SampleColumns>) => {
    safeAssert(isPopulatedSample(sample), `Sample ${sample.id} is not populated`);
  });
}

export function isFullSample(sample: NullablePartial<FullSample>): sample is FullSample {
  return isPopulatedSample(sample) && !!sample.projects && !!sample.samplingPoint;
}

export type PossibleZoomableLevel = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15;
export type PossibleCountryLevel = 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15;
export type AdminLevelKey =
  | '1' // Resistomap's custom Global level
  | '2' // Finland
  | '3' // Åland
  | '4'
  | '5'
  | '6' // Uusimaa
  | '7' // Helsinki sub-region
  | '8' // Helsinki
  | '9' // Central major district
  | '10' // Vallila
  | '11'
  | '12'
  | '13'
  | '14'
  | '15';

export const zAdminLevelKey = z.enum(['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15']);

type MinLonLat = { lon: number; lat: number };
type MaxLonLat = MinLonLat;

export type AdminAreaBoundaries = [MinLonLat, MaxLonLat]; // Accepted by mapbox

const zLonLat = z.object({
  lon: z.number(),
  lat: z.number(),
});
const zMaxLonLat = zLonLat;
const zMinLonLat = zLonLat;
const zSouthWest = zMinLonLat;
const zNorthEast = zMaxLonLat;

// Boundaries are in the strict format of [SouthWest, NorthEast]
const zAdminAreaBoundaries = z.tuple([zSouthWest, zNorthEast]);

export interface AdminAreaMetadata {
  level: number;
  name: string;
}

const zAdminAreaMetadata = z.object({
  level: z.number(),
  name: z.string(),
});

const zAdminArea = zAdminAreaMetadata.extend({
  boundaries: zAdminAreaBoundaries.optional(),
});
export type AdminArea = z.infer<typeof zAdminArea>;

export const zAdminAreaByLevel = z.record(zAdminLevelKey, zAdminArea);
export type AdminAreaByLevel = z.infer<typeof zAdminAreaByLevel>;

export type AdminAreaMetadataByLevel = {
  [key in AdminLevelKey]?: AdminAreaMetadata;
};

export interface AdminLevelWithParents extends AdminArea {
  parent?: AdminArea;
}

export const zRawSamplingPointCoreFields = z.object({
  name: z.string(),
  type: zSampleType,
  subtype: zSampleSubType.nullable(),
  country: z.string().nullable(),
  region: z.string().nullable(),
  city: z.string().nullable(),
  lat: z.number().nullable(),
  lon: z.number().nullable(),
});
export const zRawSamplingPoint = z.object({
  ...zRawSamplingPointCoreFields.shape,
  inferredLat: z.number().nullable(),
  inferredLon: z.number().nullable(),
  adminLevels: zAdminAreaByLevel.nullable(),
});
export type RawSamplingPoint = z.infer<typeof zRawSamplingPoint>;

export const zSamplingPoint = zRawSamplingPoint.extend({
  id: z.number(),
});
export type SamplingPoint = z.infer<typeof zSamplingPoint>;

export const zPatchSamplingPointRequest = zRawSamplingPoint.partial().extend({
  id: z.number().optional(),
});
export type PatchSamplingPointRequest = z.infer<typeof zPatchSamplingPointRequest>;

// HACK: originalSamplingPointNames and od that is only populated by the UI pooling code
// it is used for keeping track of the original sampling point, eg. for keeping legacy environmentNames query param intact
// despite the formation of transient pooled (site or admin level samples in createPooledSample).
// TODO: consider removing when implementing sites
export interface PooledSplPntHack extends SamplingPoint {
  originalSamplingPointNames?: string[];
  originalSamplingPointId?: number;
  poolingKey?: string;
}

/**
 * Gets the original sampling point ID from a sampling point object.
 * If the sampling point has an originalSamplingPointId, returns that.
 * Otherwise, returns the sampling point's own ID only if it's positive.
 * This prevents bogus negative IDs from being used in query parameters.
 */
export function getOriginalSamplingPointId(
  samplingPoint: SamplingPoint | PooledSplPntHack | undefined,
): number | undefined {
  if (!samplingPoint) {
    return undefined;
  }

  const pooledSmplPnt = samplingPoint as PooledSplPntHack;
  if (pooledSmplPnt.originalSamplingPointId !== undefined) {
    return pooledSmplPnt.originalSamplingPointId;
  }

  // Only use the sampling point's ID if it's positive (not a bogus negative ID)
  return samplingPoint.id > 0 ? samplingPoint.id : undefined;
}

export function getAllOriginalSamplingPointIds(
  samplingPoint1: SamplingPoint | PooledSplPntHack,
  samplingPoint2: SamplingPoint | PooledSplPntHack | undefined,
): number[] {
  const ids = compact(uniq([getOriginalSamplingPointId(samplingPoint1), getOriginalSamplingPointId(samplingPoint2)]));
  return ids;
}

export type VersionedSample = Omit<FullSample, 'abundances'> &
  Required<Pick<FullSample, 'samplingPoint'>> & { assayResults: AssayResult[]; meanCts: MeanCt[] };

export interface SampleWithSamplingPoint extends SampleToBeCreated {
  samplingPointId: number;
  samplingPoint: SamplingPoint;
}

export type SampleForCreation = Omit<RawSample, 'id'> & {
  id?: number;
};

export type FullSampleWithTime = RequiredFields<FullSample, 'time'>;

export interface WithStartAndEndDate {
  startDate: string | Date | undefined;
  endDate: string | Date | undefined;
}

// Keep in sync with api_by_python_column and rs_to_api
export interface AnalysisAbundance extends Abundance {
  sampleId: number;
  sampleNum: number;
  bioRep: BioRep;
  target: string;
  analysisVersion: string;
}

export enum HeatmapType {
  ALL = 'resistomap_all',
  DETECTED = 'resistomap_det',
  QUANTIFIED = 'resistomap_qnt',
}

export interface Correlation {
  // carrierAssay: string;
  // resistanceAssay: string;
  carrierGene: string;
  resistanceGene: string;
  target: string;
  assayVersion: string;
  serverVersion: string;
  analysisVersion: string;
  value: number;
}

type Lat = number;
type Lon = number;
type Coordinate = [Lat, Lon];
export type AdminLevelGeodata = {
  id: number;
  localName: string;
  nameEn: string;
  countryId: string;
  adminLevel: number;
  coordinates: Coordinate[];
  boundaries: AdminAreaBoundaries | undefined; // High admin level boundaries may be missing if we haven't had credits to fetch them
};
export type AdminLevelBoundaryData = Omit<AdminLevelGeodata, 'coordinates'>;

export interface Country {
  alpha3: string;
  alpha2: string;
  name: string;
  continent: Continent;
}

export type CountryNameByAlpha3 = Record<string, string>;
export type CountryByAlpha3 = Record<string, Country>;

export function getCountryNameByAlpha3(countryByAlpha3: CountryByAlpha3): CountryNameByAlpha3 {
  return chain(countryByAlpha3)
    .mapValues(c => c.name)
    .value();
}

export enum Continent {
  AFRICA = 'AFRICA',
  ASIA = 'ASIA',
  EUROPE = 'EUROPE',
  NORTH_AMERICA = 'NORTH_AMERICA',
  OCEANIA = 'OCEANIA',
  SOUTH_AMERICA = 'SOUTH_AMERICA',
  ANTARCTICA = 'ANTARCTICA',
}

export type CsvRow = Dictionary<string | number | undefined>;
export type CsvRows = CsvRow[];

export type AbundanceByBA = Partial<{
  [key in BioRep]: Dictionary<FullAbundance>; // Not full for old projects
}>;

export type PartialDict<T> = Partial<Dictionary<T>>;

type NullablePartial<T> = {
  [P in keyof T]?: T[P] | null;
};

export type AbundancesByUBA = PartialDict<AbundanceByBA>;

export const zCoordinates = z.object({
  lat: z.number(),
  lon: z.number(),
});
export type Coordinates = z.infer<typeof zCoordinates>;

const assaySample = {
  assay: z
    .string()
    .min(3)
    .transform(val => val.trim()),
  sample: z
    .string()
    .min(1)
    .transform(val => val.trim()), // sampleId-numBiorep-rep1
};
const analysisValues = {
  ct: z
    .string()
    .nullable()
    .transform(val => (val ? parseFloat(val.replace(',', '.')) : null)),
  tm: z
    .string()
    .nullable()
    .transform(val => (val ? parseFloat(val.replace(',', '.')) : null)),
  efficiency: z
    .string()
    .nullable()
    .transform(val => (val ? parseFloat(val.replace(',', '.')) : null)),
  flags: z.string().nullable(),
};
export const zAnalysisCtValue = z.object({
  ...assaySample,
  sampleId: z.number(),
  chipId: z.number(),
  ...analysisValues,
});
export type AnalysisCtValue = z.infer<typeof zAnalysisCtValue>;

export enum QaLabelType {
  NO_PRODUCT = 'NO_PRODUCT',
  PRODUCT = 'PRODUCT',
  PRIMER_DIMER = 'PRIMER_DIMER',
  UNSPECIFIC = 'UNSPECIFIC',
  NONSENSE = 'NONSENSE',
  ISSUE = 'ISSUE',
  OTHER = 'OTHER',
}
export const zQaLabelType = z.nativeEnum(QaLabelType);

export enum QaLabelScope {
  CHIP = 'CHIP',
  TECHREP = 'TECHREP',
  TRIPLICATE = 'TRIPLICATE',
  SAMPLE = 'SAMPLE',
  ASSAY = 'ASSAY',
}
export const zQaLabelScope = z.nativeEnum(QaLabelScope);

export interface QaConcreteRepRef {
  sampleId: number;
  assay: string;
  techRep: 1 | 2 | 3;
}

export const zQaLabelScopeEntry = z.object({
  sampleId: z.number().nullable(),
  assay: z.string().nullable(),
  techRep: z.number().int().min(1).max(3).nullable(),
});
export type QaLabelScopeEntry = z.infer<typeof zQaLabelScopeEntry>;

export const zQaLabel = z.object({
  id: z.number(),
  chipName: z.string(),
  assay: z.string().nullable(),
  sampleId: z.number().nullable(),
  url: z.string().trim().min(1),
  labelBatchId: z.string(),
  labelType: zQaLabelType,
  techRep: z.number().int().min(1).max(3).nullable(),
  tm: z.number().nullable(),
  note: z.string(),
  scope: zQaLabelScope,
  appliesToResults: z.boolean(),
  userId: z.number(),
  labelerFirstName: z.string().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type QaLabel = z.infer<typeof zQaLabel>;

export const zQaResolvedLabelSummary = z.object({
  ...zQaLabel.shape,
  effectiveForResults: z.boolean(),
  overriddenByLabelId: z.number().nullable(),
});
export type QaResolvedLabelSummary = z.infer<typeof zQaResolvedLabelSummary>;

// We cannot extend, because we want to maintain key (column) order lof the origincal cts file
export const zCtRow = z.object({
  row: z.string().transform(val => parseInt(val, 10)),
  column: z.string().transform(val => parseInt(val, 10)),
  ...assaySample,
  conc: z
    .string()
    .nullable()
    .transform(val => (val ? parseFloat(val.replace(',', '.')) : null)),
  ...analysisValues,
});
export type CtBaseRow = z.infer<typeof zCtRow>;
export type CtCsvRow = CtBaseRow & { idx?: number | null; dye?: string | null; curveQuality?: number | null };
export type CtCsvRowWithSampleAndChipId = CtCsvRow & { sampleId: number | null; chipId: number };
export const ctsHeaders = keys(zCtRow.shape) as Array<keyof CtBaseRow>;
export const zCtTableColumns = z.object({
  id: z.number(),
  chipId: z.number(),
  sampleId: z.number().nullable(),
  idx: z.number().nullable().optional(),
  dye: z.string().nullable().optional(),
  curveQuality: z.number().nullable().optional(),
  ...zCtRow.shape,
  qaRuleId: z.number().nullable().optional(),
  qaRuleDescription: z.string().nullable().optional(),
  qaResultLabelType: zQaLabelType.nullable().optional(),
  qaResultLabelScope: zQaLabelScope.nullable().optional(),
  qaLabels: z.array(zQaResolvedLabelSummary).optional(),
});
export type CtsColumns = z.infer<typeof zCtTableColumns>;

export enum Flags {
  TRACES = 'TRACES',
  UNCLEAR = 'UNCLEAR',
  UNQUANTIFIED = 'UNQUANTIFIED',
}
export const zFlags = z.nativeEnum(Flags);

// Keep consistent with rs_to_api
export const zAbundance = z.object({
  gene: z.string(),
  assay: z.string(),
  assayVersion: z.string(),
  serverVersion: z.string(),
  analysisVersion: z.string(),
  measured: z.boolean(),
  relative: z.number().nullable(),
  estimate: z.number().nullable().optional(),
  estimateLow: z.number().nullable().optional(),
  estimateHigh: z.number().nullable().optional(),
  lowerBound: z.number().nullable().optional(),
  upperBound: z.number().nullable().optional(),
  meanCt: z.number().nullable(),
  flag: zFlags.nullable(),
});
export type Abundance = z.infer<typeof zAbundance>;

export const zFullAbundance = zAbundance.extend({
  absolute: z.number().nullable(),
  copiesPerL: z.number().nullable(),
  copiesPerHour: z.number().nullable(),
  copiesPerMgSS: z.number().nullable(),
  copiesPerMgBod: z.number().nullable(),
  valuesByMode: z.record(z.string(), z.number().nullable()).optional(),
  flagsByMode: z.record(z.string(), zFlags.nullable()).optional(),
  // NOTE: cts may include duplicates for AY1
  cts: z.array(zCtTableColumns).optional(),
  qaStdCt: z.number().nullable().optional(),
  techRepValuesByMode: z.record(z.string(), z.array(z.number().nullable())).optional(),
  // Estimates per normalisation mode. We store them in nested objects keyed by mode
  // to avoid inflating the top-level namespace and to keep backward compatibility
  // with the historical flat `estimate`, `estimateLow`, and `estimateHigh` fields
  // that correspond to the SIXTEEN_S mode.
  estimates: z.record(z.string(), z.number().nullable()).optional(),
  estimateLows: z.record(z.string(), z.number().nullable()).optional(),
  estimateHighs: z.record(z.string(), z.number().nullable()).optional(),
  lowerBounds: z.record(z.string(), z.number().nullable()).optional(),
  upperBounds: z.record(z.string(), z.number().nullable()).optional(),
});
export type FullAbundance = z.infer<typeof zFullAbundance>;
export type StrippedFullAbundance = Pick<
  FullAbundance,
  | 'assay'
  | 'gene'
  | 'relative'
  | 'absolute'
  | 'copiesPerL'
  | 'copiesPerHour'
  | 'copiesPerMgSS'
  | 'copiesPerMgBod'
  | 'valuesByMode'
>;

export enum LabWorkbookStatus {
  DRAFT = 'DRAFT',
  SAMPLE_SELECTION = 'SAMPLE_SELECTION',
  SUBMITTED = 'SUBMITTED',
  MEASURED = 'MEASURED',
  PRIMERS_PREPPED = 'PRIMERS_PREPPED',
  SAMPLES_PREPPED = 'SAMPLES_PREPPED',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  LEGACY = 'LEGACY',
}
export const zLabWorkbookStatus = z.nativeEnum(LabWorkbookStatus);

export const zLabWorkbook = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
  projectLead: z.string().nullable(),
  channelUrl: z.string().nullable(),
  status: zLabWorkbookStatus,
  genePanelId: z.number().int().positive().nullable(),
  sampleSheetId: z.string().uuid(),
  assignedLabOrganisationId: z.number().int().positive().nullable(),
  labSheetUrl: z.string().nullable(),
  numChips: z.number().int().min(1),
  firstSampleNumber: z.number().int().positive(),
  prescreening: z.boolean(),
  oneHealthOverride: z.boolean(),
  primerInventory: z.enum(['2024', '600']),
  defaultMmLot: z.string().nullable(),
  primerStorageFindLocation: z.string().nullable(),
  primerStorageLeaveLocation: z.string().nullable(),
  sampleStorageFindLocation: z.string().nullable(),
  sampleStorageLeaveLocation: z.string().nullable(),
  notes: z.string().nullable(),
  receivedMaterial: zReceivedMaterial.nullable(),
  services: zLabWorkbookServices.nullable(),
  createdBy: z.number().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type LabWorkbook = z.infer<typeof zLabWorkbook>;

export type LabWorkbookReceivedMaterial = ReceivedMaterial;
export type LabWorkbookServices = LabWorkbookService[];

const zLabInitials = z
  .string()
  .length(3, { message: 'Lab initials must be exactly three characters long.' })
  .regex(/^[A-Z]{3}$/, { message: 'Lab initials must use A-Z characters.' });

export const zLabWorkbookLog = z.object({
  id: z.number(),
  workbookId: z.string().uuid(),
  chipId: z.number().nullable(),
  payload: z.unknown(),
  serverVersion: z.string().nullable(),
  preparerInitials: zLabInitials.nullable(),
  uploaderInitials: zLabInitials.nullable(),
  createdBy: z.number().nullable(),
  createdAt: z.date(),
});
export type LabWorkbookLog = z.infer<typeof zLabWorkbookLog>;

export interface LabWorkbookLink {
  id: string;
  name: string | null;
  sampleSheetId: string;
  sampleSheetName: string | null;
  organisationId: number;
  organisationName: string;
}

export const zChip = z.object({
  id: z.number(),
  name: z.string(),
  mmLot: z.string().nullable(),
  waterLot: z.string().nullable(),
  glycerolLot: z.string().nullable(),
  antifoamBLot: z.string().nullable(),
  kitLot: z.string().nullable(),
  comment: z.string().nullable(),
  invoice: z.string().nullable(),
  preparerInitials: z.string().nullable(),
  uploaderInitials: z.string().nullable(),
  runDate: z.date().nullable(),
  labSheetUrl: z.string().nullable(),
  legacyProjectId: z.number().nullable(),
  dispenser: z.number().nullable(),
  dispenserPrefix: zDispenserPrefix.nullable(),
  cycler: z.number().nullable(),
  labWorkbookId: z.string().uuid().nullable(),
});
export type Chip = z.infer<typeof zChip>;

export enum ProcessMode {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
  DURING = 'DURING',
}
export const zProcessMode = z.nativeEnum(ProcessMode);

// RESEARCH VIEW (AND OVERVIEW) ABUNDANCE UNITS
export enum AbundanceMode {
  COPIES_PER_L = 'COPIES_PER_L', // NormalisationMode.LITRE_16S
  COPIES_ANALYSED = 'COPIES_ANALYSED', // NormalisationMode.TEN_UL_DILUTED_DNA_16S
  RELATIVE_ABUNDANCE = 'RELATIVE_ABUNDANCE', // NormalisationMode.SIXTEEN_S
}
export const zAbundanceMode = z.nativeEnum(AbundanceMode);

// OVERVIEW SCORE METRICS
// The MetricModes order matters for default metric selection. ARGI the default one and reduction the last choice
export enum MetricMode {
  ARGI = 'ARGI', // NormalisationMode.SIXTEEN_S
  RISK = 'RISK', // NormalisationMode.LITRE_16S
  REDUCTION = 'REDUCTION', // NormalisationMode.LITRE_16S
}
export const zMetricMode = z.nativeEnum(MetricMode);

// TODO rename MetricMode to ScoreMode and ChartDisplayMode to MetricMode and METRIC TO SCORE
// (call the superset of abundances and scores as metrics)
export enum ChartDisplayMode {
  METRIC = 'METRIC', // ARGI, RISK, OR REDUCTION
  ABUNDANCES = 'ABUNDANCES', // GENE COPIES
}

// Normalization modes in priority order
// TODO deprecate either NormalisationMode or AbundanceMode in favour of the other
export enum NormalisationMode {
  // In use
  LITRE_16S = 'LITRE_16S', // Copies per litre using AY1/16S standard curve
  TEN_UL_DILUTED_DNA_16S = 'TEN_UL_DILUTED_DNA_16S', // copies in analysed DNA using AY1/16S standard curve
  LITRE_AY = 'LITRE_AY', // Copies per litre using assay-specific standard curve
  TEN_UL_DILUTED_DNA_AY = 'TEN_UL_DILUTED_DNA_AY', // copies in analysed DNA using assay-specific standard curve
  SIXTEEN_S = 'SIXTEEN_S', // relative

  // To be comissioned
  MG_SS = 'MG_SS', // Copier per mg Suspendedd Solids
  HOUR = 'HOUR', // Copier per hour
  MG_BOD = 'MG_BOD', // Copier per mg Biochemical Oxygen Demand
}
export const zNormalisationMode = z.nativeEnum(NormalisationMode);

export const normalizationModeByMetricMode: Record<MetricMode, NormalisationMode> = {
  [MetricMode.ARGI]: NormalisationMode.SIXTEEN_S,
  [MetricMode.RISK]: NormalisationMode.LITRE_16S,
  [MetricMode.REDUCTION]: NormalisationMode.LITRE_16S,
};
export const normalizationModeByAbundanceMode: Record<AbundanceMode, NormalisationMode> = {
  [AbundanceMode.COPIES_PER_L]: NormalisationMode.LITRE_16S,
  [AbundanceMode.RELATIVE_ABUNDANCE]: NormalisationMode.SIXTEEN_S,
  [AbundanceMode.COPIES_ANALYSED]: NormalisationMode.TEN_UL_DILUTED_DNA_16S,
};
export const unitToNormalizationMode: Record<ReferenceMetric, NormalisationMode> = {
  ...normalizationModeByMetricMode,
  ...normalizationModeByAbundanceMode,
};

export const zReferenceMetric = z.union([zMetricMode, zAbundanceMode]);
export type ReferenceMetric = z.infer<typeof zReferenceMetric>;

const abundanceModeByNormalisationMode: Partial<Record<NormalisationMode, AbundanceMode>> = {
  [NormalisationMode.LITRE_16S]: AbundanceMode.COPIES_PER_L,
  [NormalisationMode.TEN_UL_DILUTED_DNA_16S]: AbundanceMode.COPIES_ANALYSED,
  [NormalisationMode.SIXTEEN_S]: AbundanceMode.RELATIVE_ABUNDANCE,
};
export function normalisationToAbundanceMode(normalisationMode: NormalisationMode) {
  return get(abundanceModeByNormalisationMode, normalisationMode, null);
}

// Type for raw database result with snake_case column names
export type DbVersion = {
  id: number;
  assay_version: string;
  server_version?: string;
  server_minor?: string;
  name: string;
  description: string;
  change_notification: string | null;
  created_at: Date;
  updated_at: Date;
};
export interface Version {
  id: number;
  assayVersion: string;
  serverMinor: string;
  name: string;
  description: string;
  changeNotification?: string | null;
  createdAt: Date;
  updatedAt: Date;
}
export const zPostVersionRequest = z.object({
  id: z.number().refine(n => Number.isInteger(n * 10) && n.toString().match(/^\d{4}\.\d+$/), {
    message: 'Version ID must be in format YYYY.N where N is an integer',
  }),
  assayVersion: z.string().refine(v => !!coerce(v), {
    message: 'Assay version must follow semver',
  }),
  name: z.string(),
  description: z.string(),
  changeNotification: z.string().optional(),
});
export type PostVersionRequest = z.infer<typeof zPostVersionRequest>;

// Base type definitions for Assay
export const zAssayBase = z.object({
  assay: z.string(),
  gene: z.string(),
  deprecated: z.boolean(),
  minMeltingTemperature: z.number().nullable(),
  maxMeltingTemperature: z.number().nullable(),
  standardCurveSlope: z.number().nullable(),
  standardCurveIntercept: z.number().nullable(),
  standardCurveMinTm: z.number().nullable(),
  standardCurveMaxTm: z.number().nullable(),
  loqCq: z.number().lt(28).nullable(),
  targetGroupLvl1: zL1Targets,
  targetGroupLvl2: zL2Targets,
  targetGroupLvl3: z.string().nullable(),
  targetGroupLvl4: z.string().nullable(),
  targetGroupLvl5: z.string().nullable(),
  genePackage1: z.boolean(),
  genePackage2: z.boolean(),
  genePackage21: z.boolean(),
  genePackage3: z.boolean(),
  forwardPrimer: z.string().nullable(),
  reversePrimer: z.string().nullable(),
  primerRef: z.string().nullable(),
  primerRefUrl: z.string().nullable(),
  revPrimerRef: z.string().nullable(),
  revPrimerRefUrl: z.string().nullable(),
  appliedRules: z.array(z.number().int()).nullable(),
});
export type AssayBase = z.infer<typeof zAssayBase>;

export const zAssayRaw = zAssayBase.extend({
  version: z.string(),
});

export type AssayRaw = z.infer<typeof zAssayRaw>;

export interface Assay extends AssayRaw {
  createdAt: Date;
}

export type RawMeanCt = {
  versionId: number;
  sampleId: number;
  assay: string;
  meanCt: number | null;
  lowerBound: number | null;
  upperBound: number | null;
  flag: Flags | null;
  serverVersion: string | null; // Populated only for AY 1, for manual investigations only
};

export interface MeanCt extends RawMeanCt {
  gene?: string;
  createdAt: Date;
}

export interface RawAssayResult {
  versionId: number;
  sampleId: number;
  assay: string;
  normalisationMode: NormalisationMode;
  value: number | null;
  measured: boolean;
  estimate?: number | null;
  estimateLow?: number | null;
  estimateHigh?: number | null;
  lowerBound?: number | null;
  upperBound?: number | null;
  flag: Flags | null;
}

export interface AssayResult extends RawAssayResult {
  gene?: string;
  estimate: number | null;
  estimateLow: number | null;
  estimateHigh: number | null;
  lowerBound: number | null;
  upperBound: number | null;
  createdAt: Date;
}

const zSampleStatusUpdate = z.object({
  id: z.number(),
  status: z.nativeEnum(SampleStatus),
});
export const zSampleStatusUpdates = z.array(zSampleStatusUpdate);
export type SampleStatusUpdates = z.infer<typeof zSampleStatusUpdates>;

// PATCH single sample
export const zPatchSampleRequest = z.object({
  id: z.number(),
  number: z.number().int().nullable().optional(),
  bioRep: zBioRep.nullable().optional(),
  // Accept both Date and ISO string for convenience
  time: z.union([z.date(), z.string()]).nullable().optional(),
  status: z.nativeEnum(SampleStatus).nullable().optional(),
  primDnaExtractionKit: z.string().nullable().optional(),
  // Normalisation and related metadata
  primDilutedDnaVolume: z.number().nullable().optional(),
  primDilutedDnaConcentration: z.number().nullable().optional(),
  primDilutedDnaQuality280: z.number().nullable().optional(),
  primDilutedDnaQuality230: z.number().nullable().optional(),
  primElutedDnaQuality280: z.number().nullable().optional(),
  primElutedDnaQuality230: z.number().nullable().optional(),
  primElutedDnaConcentration: z.number().nullable().optional(),
  primElutedDnaVolume: z.number().nullable().optional(),
  filteredVolume: z.number().nullable().optional(),
  bod: z.number().nullable().optional(),
  suspendedSolids: z.number().nullable().optional(),
  operatedFlowRate: z.number().nullable().optional(),
  flowRate: z.number().nullable().optional(),
  // Note: samplingPointId and specific coordinates are intentionally NOT supported for patching via this endpoint
});
export type PatchSampleRequest = z.infer<typeof zPatchSampleRequest>;

export enum XAxisMode {
  TIME = 'TIME',
  ORDINAL_GROUPED = 'ORDINAL_GROUPED',
  ORDINAL_INDIVIDUAL = 'ORDINAL_INDIVIDUAL',
}

export enum EntityTypes {
  PROJECT = 'PROJECT',
  SAMPLE = 'SAMPLE',
  SAMPLING_POINT = 'SAMPLING_POINT',
  SHIPMENT = 'SHIPMENT',
  VERSION = 'VERSION',
  USER = 'USER',
  ORGANISATION = 'ORGANISATION',
  QA_NOTE = 'QA_NOTE',
  QA_LABEL = 'QA_LABEL',
  GENE_PANEL = 'GENE_PANEL',
  LAB_WORKBOOK = 'LAB_WORKBOOK',
  SAMPLE_SHEET = 'SAMPLE_SHEET',
}

export interface EditLog {
  id: number;
  endpoint: string;
  entityId: number | string;
  entityType: EntityTypes;
  payload: unknown;
  userId: number;
  createdAt: Date;
}

export enum CurveType {
  AMPLIFICATION = 'AMPLIFICATION',
  ANNEALING = 'ANNEALING',
  DERIVATIVE = 'DERIVATIVE',
}
export const zCurveType = z.nativeEnum(CurveType);

export interface RawChipCurve {
  chipId: number;
  sampleId: number | null;
  assay: string;
  type: CurveType;
  xs: number[];
  ys: number[];
}

export interface ChipCurve extends RawChipCurve {
  id: number;
  createdAt: Date;
}

export interface WellDebugData {
  cts: CtsColumns;
  sampleName: string | null;
  sampleType: SampleType | null;
  sampleSubtype: SampleSubType | null;
  sampleTime: Date | null;
  amplificationCurve: ChipCurve | null;
  annealingCurve: ChipCurve | null;
  derivateCurve: ChipCurve | null;
}

export enum SmartChipFlag {
  MultipleMeltPeaks = 'MultipleMeltPeaks',
  CtIsLarge = 'CtIsLarge',
  CtIsSmall = 'CtIsSmall',
  CtIsOutlier = 'CtIsOutlier',
  ContainsOutlierReplicateCt = 'ContainsOutlierReplicateCt',
  CurveFitFailed = 'CurveFitFailed',
  LowSaturation = 'LowSaturation',
  SaturationBaselineRatioIsLow = 'SaturationBaselineRatioIsLow',
  HighBaseline = 'HighBaseline',
  NoAmplification = 'NoAmplification',
  LowEfficiency = 'LowEfficiency',
  HighEfficiency = 'HighEfficiency',
  BadR2 = 'BadR2',
}
export const availableFlags: SmartChipFlag[] = Object.values(SmartChipFlag);

export enum ChipWarningSeverity {
  WARNING = 'warning',
}
export const zChipWarningSeverity = z.nativeEnum(ChipWarningSeverity);

export enum ChipWarningKind {
  PROTOCOL_INCOMPLETE = 'PROTOCOL_INCOMPLETE',
  ASSAY_WITHOUT_AMPLIFICATION = 'ASSAY_WITHOUT_AMPLIFICATION',
  SAMPLE_WITHOUT_AMPLIFICATION = 'SAMPLE_WITHOUT_AMPLIFICATION',
  SIXTEEN_S_MISSING_WITH_OTHER_DETECTIONS = 'SIXTEEN_S_MISSING_WITH_OTHER_DETECTIONS',
  RELATIVE_ABUNDANCE_GT_ONE = 'RELATIVE_ABUNDANCE_GT_ONE',
  HIGH_FLAG_COUNTS = 'HIGH_FLAG_COUNTS',
}
export const zChipWarningKind = z.nativeEnum(ChipWarningKind);

export const zChipWarningFocusTarget = z.object({
  label: z.string(),
  assay: z.string().optional(),
  biologicalSample: z.string().optional(),
  technicalSample: z.string().optional(),
  flag: z.nativeEnum(SmartChipFlag).optional(),
});
export type ChipWarningFocusTarget = z.infer<typeof zChipWarningFocusTarget>;

export const zChipWarning = z.object({
  id: z.string(),
  kind: zChipWarningKind,
  severity: zChipWarningSeverity,
  message: z.string(),
  focusTargets: z.array(zChipWarningFocusTarget),
});
export type ChipWarning = z.infer<typeof zChipWarning>;
export interface ChipDebugProject {
  id: number;
  name: string;
}

export interface ChipDebugResponse {
  chip: Chip;
  debugData: WellDebugData[];
  projects: ChipDebugProject[];
  qaLabels: QaLabel[];
  workbook: LabWorkbookLink | null;
  warnings: ChipWarning[];
}

export const zCardIndex = z.object({
  id: z.number(),
  cardVersion: z.string(),
  aroAccession: z.string().nullable(),
  cvtermId: z.number().nullable(),
  modelSequenceId: z.number().nullable(),
  modelId: z.number().nullable(),
  modelName: z.string().nullable(),
  aroName: z.string().nullable(),
  proteinAccession: z.string().nullable(),
  dnaAccession: z.string().nullable(),
  amrGeneFamily: z.string().nullable(),
  drugClass: z.string().nullable(),
  resistanceMechanism: z.string().nullable(),
  cardShortName: z.string().nullable(),
  createdAt: z.date(),
});
export type CardIndex = z.infer<typeof zCardIndex>;

export const aroCatAmrGeneFamily = 'AMR Gene Family' as const;
export const aroCatResistanceMechanism = 'Resistance Mechanism' as const;
export const aroCatDrugClass = 'Drug Class' as const;
export type CardCategoryType = typeof aroCatAmrGeneFamily | typeof aroCatResistanceMechanism | typeof aroCatDrugClass;

export const zCardCategory = z.object({
  id: z.number(),
  cardVersion: z.string(),
  aroCategory: z.string().nullable(),
  aroAccession: z.string().nullable(),
  cvtermId: z.number().nullable(),
  aroName: z.string().nullable(),
  createdAt: z.date(),
});
export type CardCategory = Omit<z.infer<typeof zCardCategory>, 'aroCategory'> & {
  aroCategory: CardCategoryType | null;
};

export const zCardCategoryIndex = z.object({
  id: z.number(),
  cardVersion: z.string(),
  proteinAccession: z.string().nullable(),
  dnaAccession: z.string().nullable(),
  amrGeneFamily: z.string().nullable(),
  drugClass: z.string().nullable(),
  resistanceMechanism: z.string().nullable(),
  createdAt: z.date(),
});
export type CardCategoryIndex = z.infer<typeof zCardCategoryIndex>;

export interface AssayMatchInstance {
  forwardTemplate: string;
  forwardTemplateStart: number;
  reverseTemplate: string;
  reverseTemplateStart: number;
}

export interface FullAssayMatchInstance extends AssayMatchInstance {
  productLength: number;
  forwardTemplateEnd: number;
  reverseTemplateEnd: number;
}

export interface AssayMatch {
  id: number;
  db: GeneDatabase;
  assay: string;
  accession: string | null;
  minMismatches: number | null;
  matches: AssayMatchInstance[] | null;
}

export enum GeneDatabase {
  card = 'card',
  nt_core = 'nt_core',
  nt = 'nt',
}

export interface NcbiRecord {
  id: number;
  database: GeneDatabase;
  accession: string;
  payload: NcbiEsummaryRecord;
  createdAt: Date;
}

export interface CardCategoryStat {
  type: CardCategoryType;
  name: string;
  aroAccession: string | null;
  cvtermId: number | null;
  totalGenes: number;
  coveredGenes: number;
}

export interface CardAssayInfo {
  matches: CardIndex[];
  amrFamilies: CardCategoryStat[];
}
